import React, { useState, useEffect, useRef } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { TutorialObjective, TutorialStep } from '../../systems/tutorial/TutorialSystem';

/**
 * TutorialOverlay — the FTUE beat toast (Hollow Coil Lane F, S2).
 *
 * One-sentence, top-center, non-modal prompt for the active tutorial beat.
 * Never blocks play (pointer-events: none). `**text**` segments in the beat
 * description render bold (the hotkey glyphs — the only formatting the design
 * allows). Driven by TutorialSystem's `tutorial:step_changed` /
 * `tutorial:objective_completed` events; pulls the live beat on mount so it
 * can't miss a beat shown before React mounted.
 */

/** Render `**bold**` segments (odd split indices are the bold ones). */
const renderBeatText = (text: string): React.ReactNode =>
  text.split('**').map((seg, i) =>
    i % 2 === 1 ? <strong key={i} style={{ color: '#ffd700' }}>{seg}</strong> : <span key={i}>{seg}</span>
  );

const COMPLETED_LINGER_MS = 1500;

const SCREEN_READER_ONLY_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export const TutorialOverlay: React.FC = () => {
  const [objective, setObjective] = useState<TutorialObjective | null>(null);
  const [justCompleted, setJustCompleted] = useState(false);
  const lingerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const eventBus = EventBus.getInstance();

    const clearLinger = () => {
      if (lingerTimer.current) {
        clearTimeout(lingerTimer.current);
        lingerTimer.current = null;
      }
    };

    const handleStepChanged = (data: { step: string; objective?: TutorialObjective }) => {
      clearLinger();
      if (data.step === TutorialStep.COMPLETED || !data.objective) {
        setObjective(null);
        setJustCompleted(false);
        return;
      }
      setObjective(data.objective);
      setJustCompleted(false);
    };

    // Show the ✅ state briefly, then hide — unless a queued beat replaced it
    // (its step_changed clears the linger timer above).
    const handleObjectiveCompleted = (data: { step: string; objective?: TutorialObjective }) => {
      if (data.objective) setObjective(data.objective);
      setJustCompleted(true);
      clearLinger();
      lingerTimer.current = setTimeout(() => {
        setObjective(null);
        setJustCompleted(false);
      }, COMPLETED_LINGER_MS);
    };

    const unsubStep = eventBus.on(EVENTS.TUTORIAL_STEP_CHANGED, handleStepChanged);
    const unsubDone = eventBus.on(EVENTS.TUTORIAL_OBJECTIVE_COMPLETED, handleObjectiveCompleted);

    // Populate only after the empty live region has mounted. Pulling after
    // subscription also recovers any beat emitted before React attached.
    const ts = ServiceRegistry.getInstance().get('TutorialSystem', { optional: true });
    const currentObjective = ts?.getActiveObjective?.() ?? null;
    if (currentObjective) setObjective(currentObjective);

    return () => {
      unsubStep();
      unsubDone();
      clearLinger();
    };
  }, []);

  const overlayStyle: React.CSSProperties = objective
    ? {
      position: 'fixed',
      top: '18px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 'min(560px, calc(100vw - 32px))',
      boxSizing: 'border-box',
      background: 'rgba(0, 0, 0, 0.72)',
      backdropFilter: 'blur(8px)',
      border: `1px solid ${justCompleted ? 'rgba(140, 220, 140, 0.8)' : 'rgba(255, 215, 0, 0.7)'}`,
      borderRadius: '10px',
      padding: '10px 22px',
      color: 'white',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
      animation: 'ftueSlideIn 0.4s ease-out',
      pointerEvents: 'none', // never blocks play — prompts, not modals
      fontSize: '1rem',
      lineHeight: 1.4,
      textAlign: 'center',
      overflowWrap: 'anywhere',
    }
    : {
      ...SCREEN_READER_ONLY_STYLE,
      pointerEvents: 'none',
    };

  return (
    <>
      <div
        className="tutorial-overlay"
        data-visible={objective ? 'true' : 'false'}
        role="status"
        aria-live="polite"
        aria-atomic={false}
        aria-relevant="additions text"
        style={overlayStyle}
      >
        {objective && (
          <>
            <span aria-hidden="true">{justCompleted ? '✅' : '🎯'}</span>
            <span>
              {justCompleted && (
                <span style={SCREEN_READER_ONLY_STYLE}>Objective complete: </span>
              )}
              {renderBeatText(objective.description)}
            </span>
          </>
        )}
      </div>
      <style>{`
        @keyframes ftueSlideIn {
          from { transform: translate(-50%, -120%); opacity: 0; }
          to { transform: translate(-50%, 0); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .tutorial-overlay {
            animation: none !important;
          }
        }
      `}</style>
    </>
  );
};
